<template>
  <div class="detail">
    <section class="main container">
      <div class="gallery">
        <div class="thumbs">
          <div class="scroll-up" v-if="product.images.length > maxVisibleThumbs" :class="{ disabled: !canScrollUp }" @click="scrollUp">
            <i class="iconfont icon-zhankai1"></i>
          </div>
          <div class="thumbs-list">
            <ElImage
              v-for="(t, i) in visibleThumbs"
              :key="scrollOffset + i"
              :src="t"
              fit="cover"
              :class="['thumb', { active: scrollOffset + i === active }]"
              @click="active = scrollOffset + i"
            />
          </div>
          <div class="scroll-down" v-if="product.images.length > maxVisibleThumbs" :class="{ disabled: !canScrollDown }" @click="scrollDown">
            <i class="iconfont icon-shouqi1"></i>
          </div>
        </div>
        <div class="cover">
          <ElImage
            :src="product.images[active]"
            fit="cover"
            class="cover-img"
          />
        </div>
      </div>

      <div class="info">
        <h1 class="title">{{ product.name }} <span class="sold-out-tag" v-if="showArrivalNotification">售罄</span></h1>
        <div class="meta-line">产品编号：{{ product.prodNo }}</div>
        <div class="meta-line">主石类型：{{ product.stoneType }}</div>
        <div class="meta-line">支持定制：{{ product.supportDeposit ? '支持' : '不支持' }}</div>
        <div class="meta-category" v-if="product.categoryName">
          {{ product.categoryName }}
        </div>
        <div class="price">
          <div class="member-price">会员价：￥{{ displayMemberPrice }} <span class="tax">{{ currencyType }}</span><span class="tax">（含税）</span></div>
          <div class="original-price">原价：￥{{ displayPrice }}</div>
        </div>

        <div class="section">
          <div class="section-header" @click="openIntro = !openIntro">
            <div>
              <i class="iconfont icon-liangdian"></i>
              <span>产品亮点介绍</span>
            </div>
            <span class="section-toggle">{{ openIntro ? "－" : "＋" }}</span>
          </div>
          <div v-show="openIntro" class="desc">{{ product.brief }}</div>
        </div>

        <div class="section">
          <div class="section-header" @click="openSpecs = !openSpecs">
            <span>产品详情</span>
            <span class="section-toggle">{{ openSpecs ? "－" : "＋" }}</span>
          </div>
          <div v-show="openSpecs" class="table" v-if="specs.length">
            <div class="row" v-for="s in specs" :key="s.k">
              <div class="cell k">{{ s.k }}</div>
              <div class="cell v">{{ s.v }}</div>
            </div>
          </div>
        </div>

        <div class="choose">
          <div class="field full" v-for="k in propKeys" :key="k">
            <label class="label">{{ k }}</label>
            <ElSelect
              v-model="selectedPropObj[k]"
              placeholder="请选择"
              class="full-input"
              @change="onPropChange"
            >
              <ElOption
                v-for="opt in skuGroup[k]"
                :key="opt"
                :label="opt"
                :value="opt"
                :disabled="isOptionDisabled(k, opt)"
              />
            </ElSelect>
          </div>
          <div class="field">
            <label class="label">数量</label>
            <ElInputNumber v-model="qty" :min="1" class="qty-input" />
          </div>
        </div>

        <ElButton
          v-if="buyNowEnabled"
          class="buy"
          @click="buyNow"
        >立即下单</ElButton>
        <ElButton
          v-else-if="showArrivalNotification"
          class="buy"
          @click="notifyArrival"
        >到货通知</ElButton>
        <ElButton
          v-else
          class="buy"
          @click="addItemToCart"
        >加入购物车</ElButton>

        <div class="share">
          <span>分享</span>
          <div class="share-content">
          <i class="iconfont icon-a-Facebook-Negative share-icon" @click="share('facebook')"></i>
          <i class="iconfont icon-a-Instagram-Negative share-icon" @click="share('instagram')"></i>
          <i class="iconfont icon-a-TikTok-Negative share-icon" @click="share('tiktok')"></i>
          <i class="iconfont icon-a-Telegram-Negative share-icon" @click="share('telegram')"></i>
          <i class="iconfont icon-a-Pinterest-Negative share-icon" @click="share('pinterest')"></i>
          </div>
        </div>
      </div>
    </section>

    <RecommendSection v-if="recommends.length > 0" :items="recommends" />

    <AppFooter />
    <CheckoutModal
      v-model="showCheckout"
      :total="buyTotal"
      :address="address"
      :address-list="addressList"
      :order-id="checkoutOrderId"
      :order-number="checkoutOrderNumber"
      :support-deposit="Number((raw as any)?.supportDeposit ?? 0)"
      :create-time="checkoutCreateTime"
      @update:address="setAddress"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import AppFooter from "@/components/AppFooter.vue";
import RecommendSection from "@/components/RecommendSection.vue";
import { useRoute, useRouter, useHead } from "#imports";
import { ElMessage } from "element-plus";
import CheckoutModal from "@/components/CheckoutModal.vue";
const api = useApi();
const route = useRoute();
const router = useRouter();
const auth = useAuth();
const openIntro = ref(true);
const openSpecs = ref(true);
const prodId = Number(route.params.id);
const resp = await api.get<any>(
  "/prod/prodInfo",
  { prodId },
  { server: false, key: `goods-prodInfo-${prodId}`, retry: 0 }
);
const raw = (resp as any)?.data ?? (resp as any)?.raw ?? {};
const images = (() => {
  const s = raw?.imgs ?? raw?.imagesStr ?? raw?.picsStr;
  if (typeof s === "string") {
    return s.split(/[\s,|]+/).filter(Boolean);
  }
  if (Array.isArray(raw?.images)) return raw.images;
  if (Array.isArray(raw?.pics)) return raw.pics;
  const single = raw?.img ?? raw?.imgUrl ?? raw?.skuImg ?? raw?.cover;
  return [single].filter(Boolean);
})();
const product = ref({
  id: raw?.id ?? raw?.prodId ?? prodId,
  name: raw?.name ?? raw?.prodName ?? raw?.spuName ?? "商品",
  price:
    raw?.price ?? raw?.actualPrice ?? raw?.minPrice ?? raw?.originPrice ?? 0,
  memberPrice:
    raw?.memberPrice ?? raw?.vipPrice ?? 0,
  images,
  stoneType:
    raw?.stoneType ??
    raw?.mainStoneType ??
    raw?.mainStoneName ??
    raw?.stoneName ??
    "",
  brief:
    raw?.brief ??
    raw?.prodBrief ??
    raw?.simpleIntro ??
    raw?.intro ??
    raw?.desc ??
    raw?.description ??
    "",
  categoryName:
    raw?.categoryName ??
    raw?.category?.categoryName ??
    raw?.cateName ??
    raw?.catName ??
    "",
  desc: raw?.desc ?? raw?.detail ?? raw?.description ?? "",
  prodNo: raw?.prodNo ?? raw?.skuNo ?? "",
});
useHead({
  title: () =>
    `yoostone独立设计师${product.value.categoryName || ""}${
      product.value.name || ""
    }天然翡翠定制珠宝`,
});
const specs = (() => {
  const arr = Array.isArray(raw?.propList) ? raw.propList : [];
  return arr
    .map((x: any) => ({
      k: String(x?.propName || ""),
      v: String(x?.propValue || ""),
    }))
    .filter((it) => it.k && it.v);
})();
const qty = ref(1);
const active = ref(0);
const scrollOffset = ref(0);
const { currencyType } = useCurrency();
const maxVisibleThumbs = 5;
const thumbHeight = 126;
const thumbGap = 0.2;
const totalThumbHeight = thumbHeight + thumbGap;

const visibleThumbs = computed(() => {
  if (product.value.images.length <= maxVisibleThumbs) {
    return product.value.images;
  }
  return product.value.images.slice(scrollOffset.value, scrollOffset.value + maxVisibleThumbs);
});

const canScrollUp = computed(() => scrollOffset.value > 0);
const canScrollDown = computed(() => scrollOffset.value + maxVisibleThumbs < product.value.images.length);

const scrollUp = () => {
  if (canScrollUp.value) {
    scrollOffset.value--;
  }
};

const scrollDown = () => {
  if (canScrollDown.value) {
    scrollOffset.value++;
  }
};


const skuList = Array.isArray(raw?.skuList) ? raw.skuList : [];
const parsedSkuList = skuList.map((sku: any) => {
  const properties: string = sku?.properties || "";
  const propMap: Record<string, string> = {};
  properties.split(";").forEach((p: string) => {
    const [k, v] = p.split(":");
    if (k && v) propMap[k] = v;
  });
  return {
    ...sku,
    propMap,
    stockVal: Number(sku?.stocks ?? sku?.stock ?? sku?.quantity ?? 0),
  };
});

const isOptionDisabled = (key: string, value: string) => {
  const currentSelections = { ...selectedPropObj.value };
  delete currentSelections[key];
  return !parsedSkuList.some((sku) => {
    if (sku.stockVal <= 0) return false;
    if (sku.propMap[key] !== value) return false;
    for (const [selKey, selVal] of Object.entries(currentSelections)) {
      if (sku.propMap[selKey] && sku.propMap[selKey] !== selVal) return false;
    }
    return true;
  });
};

const defaultPrice = Number(product.value.price);
const defaultSku = ref<any | null>(null);
const findSku = ref(false);
const skuGroup = ref<Record<string, string[]>>({});
const propKeys = ref<string[]>([]);
const selectedPropObj = ref<Record<string, string>>({});
const selectedPropObjList: Record<string, string>[] = [];
const skuGroupList = ref<Record<string, string[]>[]>([]);
const allProperties = ref<string[]>([]);
const selectedPropList = ref<string[]>([]);
const displayPrice = computed(() => {
  const sku: any = defaultSku.value;
  if (findSku.value && sku) {
    const p =
      sku?.skuPrice ??
      sku?.price ??
      sku?.actualPrice ??
      sku?.salePrice ??
      product.value.price;
    const n = Number(p);
    return Number.isFinite(n) ? n : product.value.price;
  }
  return product.value.price;
});

const displayMemberPrice = computed(() => {
  const sku: any = defaultSku.value;
  if (findSku.value && sku) {
    const p =
      sku?.memberPrice ??
      sku?.memberPrice ??
      product.value.memberPrice ??
      0;
    const n = Number(p);
    return Number.isFinite(n) ? n : 0;
  }
  const p = product.value.memberPrice ?? 0;
  const n = Number(p);
  return Number.isFinite(n) ? n : 0;
});
const buyTotal = ref<number>(0);

const currentSkuStocks = computed(() => {
  const sku: any = defaultSku.value;
  return Number(sku?.stocks ?? sku?.stock ?? sku?.quantity ?? 0);
});

const currentSupportDeposit = computed(() => {
  return Number((raw as any)?.supportDeposit ?? (raw as any)?.supportDepositFlag ?? 0);
});

const buyNowEnabled = computed(() => {
  const sku: any = defaultSku.value;
  return !!sku && currentSkuStocks.value == 0 && currentSupportDeposit.value == 1;
});

const showArrivalNotification = computed(() => {
  const sku: any = defaultSku.value;
  return !!sku && currentSkuStocks.value == 0 && currentSupportDeposit.value == 0;
});
const unique = (arr: any[]) => {
  const map = new Map<string, any>();
  for (const it of arr) {
    const k = Object.keys(it)[0];
    map.set(k, it);
  }
  return Array.from(map.values());
};
const groupSkuProp = (list: any[], price: number) => {
  if (list.length === 1 && !list[0]?.properties) {
    defaultSku.value = list[0];
    findSku.value = true;
    return;
  }
  const _skuGroupList: Record<string, string[]>[] = [];
  const skuGroupParam: Record<string, string[]> = {};
  const _allProperties: string[] = [];
  const _propKeys: string[] = [];
  const _selectedPropObj: Record<string, string> = {};
  const selectedPropObjListParam: Record<string, string>[] = [];
  let defaultSkuParam: any = null;
  for (let i = 0; i < list.length; i++) {
    let isDefault = false;
    if (!defaultSkuParam && Number(list[i]?.price) === price) {
      defaultSkuParam = list[i];
      isDefault = true;
    }
    const properties: string = list[i]?.properties || "";
    _allProperties.push(properties);
    const propList = properties ? properties.split(";") : [];
    for (let j = 0; j < propList.length; j++) {
      const seg = propList[j].split(":");
      const k = seg[0];
      const v = seg[1];
      let props = skuGroupParam[k];
      if (k && v) {
        _propKeys.push(k);
        if (isDefault) {
          _selectedPropObj[k] = v;
          const selectedPropObjItem: Record<string, string> = {};
          selectedPropObjItem[k] = v;
          selectedPropObjListParam.push(selectedPropObjItem);
        }
      }
      if (!props) {
        props = [];
        if (v) props.push(v);
      } else {
        if (v && props.indexOf(v) === -1) {
          props.push(v);
        }
      }
      skuGroupParam[k] = props;
      const propListItem: Record<string, string[]> = {};
      propListItem[k] = props;
      _skuGroupList.push(propListItem);
    }
  }
  defaultSku.value = defaultSkuParam;
  propKeys.value = Array.from(new Set(_propKeys));
  selectedPropObj.value = _selectedPropObj;
  skuGroup.value = skuGroupParam;
  skuGroupList.value = unique(_skuGroupList);
  allProperties.value = _allProperties;
  parseSelectedObjToVals(list);
};
const parseSelectedObjToVals = (list: any[]) => {
  const selectedPropObjListParam = selectedPropObjList;
  let selectedPropertiesParam = "";
  const selectedPropListParam: string[] = [];
  const selectedPropShowListParam: string[] = [];
  for (let i = 0; i < selectedPropObjListParam.length; i++) {
    const selectedPropObjItem = selectedPropObjListParam[i];
    for (const key in selectedPropObjItem) {
      if (Object.prototype.hasOwnProperty.call(selectedPropObjItem, key)) {
        selectedPropListParam.push(key + ":" + selectedPropObjItem[key]);
        selectedPropShowListParam.push(selectedPropObjItem[key]);
        selectedPropertiesParam += key + ":" + selectedPropObjItem[key] + ";";
      }
    }
  }
  selectedPropertiesParam = selectedPropertiesParam.substring(
    0,
    selectedPropertiesParam.length - 1
  );
  selectedPropList.value = selectedPropListParam;
  let findSkuParam = false;
  for (let i = 0; i < list.length; i++) {
    if (list[i]?.properties === selectedPropertiesParam) {
      findSkuParam = true;
      defaultSku.value = list[i];
      break;
    }
  }
  findSku.value = findSkuParam;
  if (findSku.value && defaultSku.value) {
    const np = Number(defaultSku.value.price);
    if (!Number.isNaN(np)) product.value.price = np;
  }
};
const onPropChange = () => {
  const selected: Record<string, string> = {};
  for (const k of propKeys.value) {
    const v = selectedPropObj.value[k];
    if (v) selected[k] = v;
  }
  selectedPropObjList.length = 0;
  for (const k in selected) {
    const o: Record<string, string> = {};
    o[k] = selected[k];
    selectedPropObjList.push(o);
  }
  parseSelectedObjToVals(skuList);
};
groupSkuProp(skuList, defaultPrice);

const notifyArrival = async () => {
  if (!isLogged()) {
    ElMessage.warning("请先登录");
    const redirect = route.fullPath || "/";
    router.push({ path: "/login", query: { redirect } });
    return;
  }

  const payload = {
    prodId: product.value.id,
  };

  const resp = await api.post<any>("/p/user/favorite", payload, {
    server: false,
    key: `favorite-prod-${product.value.id}`,
    retry: 0,
  });

  if (resp.code == '00000') {
    ElMessage.success("已添加到货通知");
  } else {
    ElMessage.error(resp.msg || "添加失败");
  }
};

const addItemToCart = async () => {
  if (!isLogged()) {
    ElMessage.warning("请先登录");
    const redirect = route.fullPath || "/";
    router.push({ path: "/login", query: { redirect } });
    return;
  }
  if (!defaultSku.value) {
    ElMessage.warning("请先选择规格");
    return;
  }
  const payload = {
    basketId: 0,
    count: qty.value,
    prodId: product.value.id,
    shopId: 1,
    skuId: (defaultSku.value as any)?.skuId,
  };
  const resp = await api.post<any>("/p/shopCart/changeItem", payload, {
    server: false,
    key: `cart-changeItem-${product.value.id}-${
      (defaultSku.value as any)?.skuId
    }`,
    retry: 0,
  });
  if (resp.code == '00000') {
    ElMessage.success("已加入购物车");
    try {
      window.dispatchEvent(new CustomEvent("cart-changed"));
    } catch {}
  } else {
    ElMessage.error(resp.msg || "加入购物车失败");
  }
};

const isLogged = () => {
  return auth.checkLogin();
};
const showCheckout = ref(false);
const address = ref<any>({});
const addressList = ref<any[]>([]);
const checkoutOrderId = ref<number | undefined>(undefined);
const checkoutOrderNumber = ref<string | undefined>(undefined);
const checkoutCreateTime = ref<string | undefined>(undefined);
const setAddress = (v: { full: string; name: string; phone: string }) => {
  address.value = v;
};
const getAddrId = async () => {
  const r = await api.get<any>("/p/address/list", {}, { server: false, key: "address-list", retry: 0 });
  const list = (r as any)?.data ?? (r as any)?.raw ?? [];
  const arr = Array.isArray(list) ? list : [];
  addressList.value = arr;
  const isDefault = (a: any) => a?.commonAddr == 1;
  const chosen = arr.find(isDefault) ?? arr[0];
  if (chosen) {
    const full = [chosen?.province, chosen?.city, chosen?.area, chosen?.addrDetail, chosen?.address, chosen?.detail].filter(Boolean).join("");
    const name = String(chosen?.receiver ?? "");
    const phone = String(chosen?.mobile ?? chosen?.tel ?? chosen?.phone ?? "");
    if (full || name || phone) address.value = { full, name, phone };
  }
  const addrId = chosen ? Number(chosen?.id ?? chosen?.addrId ?? chosen?.addressId) : undefined;
  return Number.isFinite(addrId as number) ? (addrId as number) : undefined;
};
const buyNow = async () => {
  if (!isLogged()) {
    const redirect = route.fullPath || "/";
    router.push({ path: "/login", query: { redirect } });
    return;
  }
  if (!defaultSku.value) {
    ElMessage.warning("请先选择规格");
    return;
  }
  const addrId = await getAddrId();
  const orderItem = {
    prodId: product.value.id,
    skuId: (defaultSku.value as any)?.skuId,
    prodCount: Number(qty.value),
    shopId: 1
  };
  const body: any = { orderItem };
  if (addrId !== undefined) body.addrId = addrId;
  body.supportDeposit = Number((raw as any)?.supportDeposit ?? 0);
  const resp = await api.post<any>("/p/order/confirm", body, { server: false, key: `order-confirm-${Date.now()}`, retry: 0 });
  if (resp.code == '00000') {
    const data = (resp as any)?.data ?? (resp as any)?.raw ?? {};
    buyTotal.value = data.totalDeposit;
    const hasAddr = !!(data && (data as any).userAddr && Object.keys((data as any).userAddr).length > 0);
    if (hasAddr) {
      const submitBody: any = { orderShopParam: [{ shopId: 1 }] };
      const sd = Number((raw as any)?.supportDeposit ?? 0);
      if (sd) submitBody.supportDeposit = sd;
      const submitResp = await api.post<any>("/p/order/submit", submitBody, { server: false, key: `order-submit-${Date.now()}`, retry: 0 });
      if (submitResp.code == '00000') {
        const sData = (submitResp as any)?.data ?? (submitResp as any)?.raw ?? {};
        checkoutOrderId.value = sData?.orderId;
        checkoutOrderNumber.value = sData?.orderNumber ?? sData?.orderNumbers?.split(",")?.[0];
        checkoutCreateTime.value = sData?.createTime;
        showCheckout.value = true;
      } else {
        ElMessage.error(submitResp.msg || "提交订单失败");
      }
    } else {
      checkoutOrderId.value = undefined;
      checkoutOrderNumber.value = undefined;
      showCheckout.value = true;
    }
  } else {
    ElMessage.error(resp.msg || "创建订单失败");
  }
};

const recommendRaw = Array.isArray((raw as any)?.recommedProdList)
  ? (raw as any).recommedProdList
  : Array.isArray((raw as any)?.recommendProdList)
  ? (raw as any).recommendProdList
  : Array.isArray((raw as any)?.recommends)
  ? (raw as any).recommends
  : [];
const normalizeRecommend = (p: any) => ({
  id: p?.id ?? p?.prodId ?? p?.productId ?? p?.spuId ?? Date.now(),
  name: p?.name ?? p?.prodName ?? p?.spuName ?? p?.title ?? "",
  price: p?.price ?? p?.actualPrice ?? p?.minPrice ?? p?.originPrice ?? 0,
  img: p?.img ?? p?.pic ?? p?.imgUrl ?? p?.skuImg ?? p?.cover ?? "",
});
const recommends = (recommendRaw as any[]).map((x) => normalizeRecommend(x)).filter((r) => r.img && r.name);

const shareUrl = computed(() => {
  if (typeof window === "undefined") return "";
  return window.location.href;
});
const share = async (platform: string) => {
  const url = encodeURIComponent(shareUrl.value || "");
  const title = encodeURIComponent(product.value.name || "");
  const media = encodeURIComponent(product.value.images?.[0] || "");
  let link = "";
  if (!url) return;
  if (platform === "facebook") {
    link = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
  } else if (platform === "telegram") {
    link = `https://t.me/share/url?url=${url}&text=${title}`;
  } else if (platform === "pinterest") {
    link = `https://pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${title}`;
  } else if (platform === "instagram" || platform === "tiktok") {
    try {
      await navigator.clipboard.writeText(shareUrl.value);
      ElMessage.success("商品链接已复制，可在App中分享");
      return;
    } catch {
      link = shareUrl.value;
    }
  }
  if (link) window.open(link, "_blank");
};
</script>

<style scoped>
.detail {
  font-size: 0.16rem;
}
.container {
  width: 100%;
  padding: 0 0.9rem;
}
.top {
  margin: 0.6rem auto 0;
}
.main {
  margin: 0.4rem auto 1rem;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 0.6rem;
}
.gallery {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 0.3rem;
}
.thumbs {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
}
.scroll-up,
.scroll-down {
  width: 100%;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f5f5f5;
  transition: all 0.3s;
}
.scroll-up:hover:not(.disabled),
.scroll-down:hover:not(.disabled) {
  background: #e0e0e0;
}
.scroll-up.disabled,
.scroll-down.disabled {
  cursor: not-allowed;
  opacity: 0.3;
}
.scroll-up .iconfont,
.scroll-down .iconfont {
  font-size: 0.2rem;
  color: #121212;
}
.thumbs-list {
  display: grid;
  grid-auto-rows: 126px;
  gap: 0.2rem;
  width: 100%;
}
.thumb {
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.thumb.active {
  border: 1px solid #121212;
}
.cover-img {
  width: 6.4rem;
  height: 9.6rem;
  max-width: 100%;
}
.info {
}
.title {
  font-size: 0.28rem;
  font-weight: 700;
  color: #121212;
  display: flex;
  align-items: center;
  gap: 0.1rem;
}
.sold-out-tag {
  background: #f00;
  color: #fff;
  font-size: 0.12rem;
  padding: 0.06rem 0.1rem;
  border-radius: 0.04rem;
  margin-left: 0.1rem;
}

.meta-line {
  font-size: 0.18rem;
  color: #333537;
  margin-top: 0.2rem;
}
.meta-category {
  height: 0.32rem;
  line-height: 0.32rem;
  display: inline-block;
  padding: 0 0.1rem;
  background: #07a170;
  font-size: 0.14rem;
  color: #fff;
  margin-top: 0.24rem;
}
.price {
  padding: 0.3rem 0;
  border-bottom: 1px solid #eaeaea;
}
.original-price {
  font-size: 0.24rem;
  color: #999;
  text-decoration: line-through;
  margin-top: 0.1rem;
}
.member-price {
  font-size: 0.32rem;
  color: #FF4444;
  font-weight: 600;
}
.tax {
  color: #666;
  font-size: 0.14rem;
  margin-left: 0.08rem;
}
.status {
  display: flex;
  gap: 0.2rem;
  margin-bottom: 0.2rem;
}
.section {
  margin-top: 0.18rem;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 0.16rem;
  margin-bottom: 0.24rem;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.16rem 0;
  color: #121212;
  font-size: 0.18rem;
}
.section-header .iconfont {
  margin-right: 0.16rem;
  font-size: 0.18rem;
}
.section-toggle {
  color: #999;
}
.desc {
  font-size: 0.18rem;
  line-height: 0.24rem;
  color: #424446;
  margin-bottom: 0.24rem;
}
.section-title.sm {
  font-size: 0.18rem;
  margin: 0.28rem 0 0.16rem;
}
.table {
  border: 1px solid #C9CBCD;
  overflow: hidden;
  margin-bottom: 0.24rem;
}
.row {
  display: grid;
  grid-template-columns: 1fr 2fr;
}
.cell {
  padding: 0.16rem 0.1rem;
  border-top: 1px solid #C9CBCD;
  font-size: 0.16rem;
}
.row:first-child .cell {
  border-top: none;
}
.cell.k {
  background: #F2F4F6;
  border-right: 1px solid #C9CBCD;
  color: #121212;
}
.cell.v {
  background: #fff;
}
.choose {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.2rem;
  align-items: center;
  margin: 0.3rem 0;
}
.field {
  display: grid;
  gap: 0.12rem;
}
.field.full {
  grid-column: 1 / -1;
}
.label {
  font-size: 0.18rem;
  color: #121212;
}
.full-input {
  width: 100%;
}
.full-input :deep(.el-select),
.full-input :deep(.el-select__wrapper),
.full-input :deep(.el-input__wrapper) {
  height: 0.64rem;
  min-height: 0.64rem;
  border-radius: 0 !important;
  padding: 0 0.16rem !important;
}
.full-input :deep(.el-select__selected-item),
.full-input :deep(.el-select__placeholder),
.full-input :deep(.el-input__inner) {
  height: 0.64rem;
  line-height: 0.64rem;
  font-size: 0.16rem;
}
.qty-input :deep(.el-input__wrapper) {
  height: 0.64rem !important;
  min-height: 0.64rem !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  padding: 0 0.12rem !important;
}
.qty-input :deep(.el-input-number__decrease),
.qty-input :deep(.el-input-number__increase) {
  height: 0.64rem !important;
  line-height: 0.64rem !important;
}
.buy {
  width: 100%;
  height: 0.64rem;
  background: #121212;
  border-color: #121212;
  color: #fff;
}
.share {
  margin-top: 0.6rem;
  color: #121212;
  font-size: 0.18rem;
}
.share-content{
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.2rem;
}
.share-icon {
  width: 0.32rem;
  height: 0.32rem;
  line-height: 0.32rem;
  font-size: 0.32rem;
  color: #121212;
  cursor: pointer;
}
</style>
